'use client';

import React, { useState, useEffect } from 'react';
import Button from '@/components/ui/Button';
import Icon from '@/components/ui/AppIcon';

interface ProfileSectionProps {
    user: any;
    userProfile: any;
    onUpdateProfile: (data: { full_name: string }) => Promise<{ error: any }>;
}

export default function ProfileSection({ user, userProfile, onUpdateProfile }: ProfileSectionProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [fullName, setFullName] = useState(userProfile?.full_name || '');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    useEffect(() => {
        if (userProfile?.full_name) {
            setFullName(userProfile.full_name);
        }
    }, [userProfile]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);

        const { error } = await onUpdateProfile({ full_name: fullName });

        if (error) {
            setMessage({ type: 'error', text: error.message });
        } else {
            setMessage({ type: 'success', text: 'Profile updated successfully' });
            setIsEditing(false);
        }
        setLoading(false);
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50/50">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">General Information</h2>
                    <p className="text-sm text-gray-500 mt-1">Update your personal details and public profile.</p>
                </div>
                {!isEditing && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                    >
                        <Icon name="PencilIcon" size={16} />
                        Edit Profile
                    </button>
                )}
            </div>

            <div className="p-6">
                {message && (
                    <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${message.type === 'success'
                            ? 'bg-green-50 text-green-800 border border-green-200'
                            : 'bg-red-50 text-red-800 border border-red-200'
                        }`}>
                        <Icon
                            name={message.type === 'success' ? 'CheckCircleIcon' : 'ExclamationTriangleIcon'}
                            size={20}
                            variant="outline"
                        />
                        <span>{message.text}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Avatar Section Placeholder */}
                    <div className="flex items-center gap-6">
                        <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center text-primary font-headline text-3xl font-bold border-2 border-white shadow-md">
                            {userProfile?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <div>
                            <h3 className="text-lg font-medium text-gray-900">Profile Photo</h3>
                            <p className="text-sm text-gray-500 mb-2">Customize your look.</p>
                            <button type="button" disabled className="text-sm font-medium text-primary opacity-50 cursor-not-allowed">
                                Change Photo (Coming Soon)
                            </button>
                        </div>
                    </div>

                    <div className="grid gap-x-8 gap-y-6 md:grid-cols-2">
                        <div className="space-y-1">
                            <label className="block text-sm font-semibold text-gray-700">
                                Full Name
                            </label>
                            <input
                                type="text"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                disabled={!isEditing}
                                className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 ${isEditing
                                        ? 'border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent bg-white shadow-sm'
                                        : 'border-transparent bg-gray-50 text-gray-700 cursor-default'
                                    }`}
                                placeholder="Your full name"
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="block text-sm font-semibold text-gray-700">
                                Email Address
                            </label>
                            <div className="relative">
                                <input
                                    type="email"
                                    value={user.email}
                                    disabled
                                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                                />
                                <div className="absolute left-3 top-3 text-gray-400">
                                    <Icon name="EnvelopeIcon" size={18} />
                                </div>
                                <div className="absolute right-3 top-3 text-gray-400">
                                    <Icon name="LockClosedIcon" size={16} />
                                </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1 pl-1">Contact support to change email.</p>
                        </div>

                        <div className="space-y-1">
                            <label className="block text-sm font-semibold text-gray-700">
                                Role
                            </label>
                            <div className="flex items-center h-[42px]">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-100 capitalize">
                                    {userProfile?.role || 'User'}
                                </span>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="block text-sm font-semibold text-gray-700">
                                Member Since
                            </label>
                            <div className="flex items-center h-[42px]">
                                <span className="text-gray-900 font-medium">
                                    {new Date(userProfile?.created_at || new Date()).toLocaleDateString(undefined, {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </span>
                            </div>
                        </div>
                    </div>

                    {isEditing && (
                        <div className="pt-4 border-t border-gray-100 flex gap-4 justify-end">
                            <button
                                type="button"
                                onClick={() => {
                                    setIsEditing(false);
                                    setFullName(userProfile?.full_name || '');
                                    setMessage(null);
                                }}
                                className="px-6 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <Button
                                type="submit"
                                disabled={loading}
                                className="min-w-[140px] shadow-sm"
                                isLoading={loading}
                            >
                                Save Changes
                            </Button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}
